import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Menu as MenuIcon, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// Stili per nascondere scrollbar
const scrollbarHideStyles = `
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

export interface MenuItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  onClick?: () => void;
  children?: MenuItem[];
  /** Badge numerico opzionale (es. conteggio notifiche) */
  badge?: number;
  /** Ruolo richiesto per visualizzare questa voce */
  requiredRole?: string;
  /** Visibile anche al gruppo 'admin' (oltre a superadmin) */
  /** Visibile anche al gruppo 'admin' (oltre a superadmin).
   *  Puo' essere una funzione: serve alle voci la cui visibilita' dipende da una
   *  configurazione letta a runtime, che a modulo caricato non e' ancora nota. */
  adminVisible?: boolean | (() => boolean);
}

export interface AdaptiveMenuProps {
  items: MenuItem[];
  orientation?: 'auto' | 'horizontal' | 'vertical';
  activeItemId?: string;
  onItemClick?: (itemId: string) => void;
  className?: string;
  /**
   * Larghezza minima per mostrare menu orizzontale (default: 768px)
   * Sotto questa larghezza passa a verticale/hamburger
   */
  horizontalMinWidth?: number;
  /**
   * Se true, su mobile mostra hamburger invece di verticale compatto
   */
  mobileHamburger?: boolean;
  /**
   * Se true, mostra frecce per scroll orizzontale (default: true)
   */
  showScrollArrows?: boolean;
  /**
   * Posizione sidebar: 'left' o 'right' (default: 'left')
   */
  sidebarPosition?: 'left' | 'right';
  /**
   * Titolo da mostrare nell'header del menu sandwich
   */
  headerTitle?: string;
  /**
   * Logo da mostrare nell'header (URL immagine)
   */
  headerLogo?: string;
}

/**
 * AdaptiveMenu - Menu intelligente che si adatta automaticamente al viewport
 *
 * COMPORTAMENTO:
 * - Desktop (>768px): Orizzontale con dropdown
 * - Mobile (<768px): Verticale compatto o hamburger
 * - Auto-detect: Cambia layout in base a larghezza finestra
 * - Manuale: Forza orientamento specifico
 */
const AdaptiveMenu: React.FC<AdaptiveMenuProps> = ({
  items,
  orientation = 'auto',
  activeItemId,
  onItemClick,
  className = '',
  horizontalMinWidth = 768,
  mobileHamburger = true,
  showScrollArrows = true,
  sidebarPosition = 'left',
  headerTitle = 'Menu',
  headerLogo
}) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null); // Per sottomenu annidati
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  // Determina orientamento effettivo
  const effectiveOrientation = orientation === 'auto'
    ? windowWidth >= horizontalMinWidth ? 'horizontal' : 'vertical'
    : orientation;

  const isMobile = windowWidth < horizontalMinWidth;
  const showHamburger = isMobile && mobileHamburger;

  // Monitor window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check scroll arrows visibility
  const checkScrollArrows = () => {
    const container = scrollContainerRef.current;
    if (!container || !showScrollArrows) return;

    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    setShowLeftArrow(scrollLeft > 5);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
  };

  // Monitor scroll for arrows and ensure scroll starts at left
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || effectiveOrientation !== 'horizontal') return;

    // Assicura che lo scroll parta da sinistra
    container.scrollLeft = 0;

    // Controlla frecce dopo un breve delay per permettere il render
    const initialCheck = setTimeout(() => {
      checkScrollArrows();
    }, 100);

    container.addEventListener('scroll', checkScrollArrows);
    window.addEventListener('resize', checkScrollArrows);

    return () => {
      clearTimeout(initialCheck);
      container.removeEventListener('scroll', checkScrollArrows);
      window.removeEventListener('resize', checkScrollArrows);
    };
  }, [effectiveOrientation, showScrollArrows, items]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenDropdownId(null);
        setOpenSubmenuId(null);
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleItemClick = (item: MenuItem) => {
    if (item.children && item.children.length > 0) {
      // Toggle dropdown
      const newDropdownId = openDropdownId === item.id ? null : item.id;
      setOpenDropdownId(newDropdownId);
      // Reset submenu quando si cambia dropdown
      if (newDropdownId !== openDropdownId) {
        setOpenSubmenuId(null);
      }
    } else {
      // Execute click handler
      item.onClick?.();
      onItemClick?.(item.id);
      setOpenDropdownId(null);
      setOpenSubmenuId(null);
      setMobileMenuOpen(false);
    }
  };

  const handleChildClick = (childItem: MenuItem) => {
    childItem.onClick?.();
    onItemClick?.(childItem.id);
    setOpenDropdownId(null);
    setOpenSubmenuId(null);
    setMobileMenuOpen(false);
  };

  // Drag handlers per scroll orizzontale
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setDragStartX(e.pageX - scrollContainerRef.current.offsetLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - dragStartX) * 2; // Moltiplicatore per velocità drag
    scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollLeft - walk;
    setDragStartX(x);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Calcola posizione dropdown
  const getDropdownPosition = (itemId: string) => {
    const button = buttonRefs.current[itemId];
    if (!button) return { top: 0, left: 0 };

    const rect = button.getBoundingClientRect();
    return {
      top: rect.bottom + 4,
      left: rect.left
    };
  };

  // HORIZONTAL LAYOUT
  const renderHorizontal = () => (
    <nav
      ref={menuRef}
      className={`relative bg-slate-800 border-b border-slate-700 w-full ${className}`}
    >
      {/* Freccia sinistra */}
      {showScrollArrows && showLeftArrow && (
        <button
          onClick={() => {
            scrollContainerRef.current?.scrollBy({ left: -200, behavior: 'smooth' });
          }}
          className="absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-slate-800 via-slate-800/95 to-transparent z-20 flex items-center justify-center hover:from-blue-600/80 transition-all duration-200 group"
        >
          <svg className="w-6 h-6 text-blue-400 drop-shadow-lg group-hover:text-white group-hover:scale-125 transition-all duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Freccia destra */}
      {showScrollArrows && showRightArrow && (
        <button
          onClick={() => {
            scrollContainerRef.current?.scrollBy({ left: 200, behavior: 'smooth' });
          }}
          className="absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-slate-800 via-slate-800/95 to-transparent z-20 flex items-center justify-center hover:from-blue-600/80 transition-all duration-200 group"
        >
          <svg className="w-6 h-6 text-blue-400 drop-shadow-lg group-hover:text-white group-hover:scale-125 transition-all duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      <div
        ref={scrollContainerRef}
        className={`flex items-center gap-1 py-2 overflow-x-auto scrollbar-hide ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          paddingLeft: showLeftArrow ? '48px' : '16px',
          paddingRight: showRightArrow ? '48px' : '16px'
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        {items.map(item => {
          const Icon = item.icon;
          const isActive = activeItemId === item.id;
          const hasChildren = item.children && item.children.length > 0;
          const isDropdownOpen = openDropdownId === item.id;

          return (
            <div key={item.id} className="relative flex-shrink-0">
              <button
                ref={(el) => { buttonRefs.current[item.id] = el; }}
                onMouseDown={() => {
                  // Previeni drag se si clicca sul bottone (handled by stopPropagation in parent)
                }}
                onClick={() => {
                  if (!isDragging) {
                    handleItemClick(item);
                  }
                }}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200
                  ${isActive
                    ? 'bg-blue-600 text-white font-semibold'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }
                `}
              >
                {Icon && <Icon size={18} />}
                <span className="whitespace-nowrap">{item.label}</span>
                {item.badge !== undefined && item.badge > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full min-w-[18px] text-center">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
                {hasChildren && (
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                  />
                )}
              </button>

              {/* Dropdown */}
              {hasChildren && isDropdownOpen && (() => {
                const pos = getDropdownPosition(item.id);
                return (
                  <div
                    className="fixed min-w-[200px] bg-slate-700 border border-slate-600 rounded-lg shadow-xl animate-in slide-in-from-top-2 fade-in duration-200"
                    style={{
                      zIndex: 9999,
                      top: pos.top,
                      left: pos.left
                    }}
                  >
                  {item.children!.map(child => {
                    const ChildIcon = child.icon;
                    const isChildActive = activeItemId === child.id;
                    const hasGrandchildren = child.children && child.children.length > 0;
                    const isSubmenuOpen = openSubmenuId === child.id;

                    return (
                      <div key={child.id} className="relative">
                        <button
                          onClick={() => {
                            if (hasGrandchildren) {
                              setOpenSubmenuId(isSubmenuOpen ? null : child.id);
                            } else {
                              handleChildClick(child);
                            }
                          }}
                          className={`
                            w-full flex items-center justify-between gap-3 px-4 py-2 text-left transition-colors
                            ${isChildActive || isSubmenuOpen
                              ? 'bg-blue-600/30 text-blue-300 font-semibold'
                              : 'text-slate-300 hover:bg-slate-600 hover:text-white'
                            }
                            first:rounded-t-lg last:rounded-b-lg
                          `}
                        >
                          <div className="flex items-center gap-3">
                            {ChildIcon && <ChildIcon size={16} />}
                            <span>{child.label}</span>
                            {child.badge !== undefined && child.badge > 0 && (
                              <span className="ml-1 px-1.5 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full min-w-[18px] text-center">
                                {child.badge > 99 ? '99+' : child.badge}
                              </span>
                            )}
                          </div>
                          {hasGrandchildren && (
                            <ChevronDown
                              size={14}
                              className={`transition-transform duration-200 ${isSubmenuOpen ? 'rotate-180' : ''}`}
                            />
                          )}
                        </button>

                        {/* Sottomenu annidato (terzo livello) */}
                        {hasGrandchildren && isSubmenuOpen && (
                          <div className="bg-slate-600 border-t border-slate-500">
                            {child.children!.map(grandchild => {
                              const GrandchildIcon = grandchild.icon;
                              const isGrandchildActive = activeItemId === grandchild.id;

                              return (
                                <button
                                  key={grandchild.id}
                                  onClick={() => handleChildClick(grandchild)}
                                  className={`
                                    w-full flex items-center gap-3 px-6 py-2 text-left transition-colors
                                    ${isGrandchildActive
                                      ? 'bg-blue-600/40 text-blue-200 font-semibold'
                                      : 'text-slate-200 hover:bg-slate-500 hover:text-white'
                                    }
                                  `}
                                >
                                  {GrandchildIcon && <GrandchildIcon size={14} />}
                                  <span className="text-sm">{grandchild.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>
    </nav>
  );

  // VERTICAL LAYOUT (Sandwich Menu)
  const renderVertical = () => {
    const isLeft = sidebarPosition === 'left';
    const hamburgerPosition = isLeft ? 'left-4' : 'right-4';
    const sidebarSide = isLeft ? 'left-0' : 'right-0';
    const borderClass = isLeft ? 'border-r' : 'border-l';
    const translateHidden = isLeft ? '-translate-x-full' : 'translate-x-full';

    return (
      <>
        {/* Hamburger Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={`fixed top-4 ${hamburgerPosition} z-50 p-2 bg-slate-800 border border-slate-700 rounded-lg hover:bg-slate-700 transition-colors`}
        >
          {mobileMenuOpen ? (
            <X size={24} className="text-white" />
          ) : (
            <MenuIcon size={24} className="text-white" />
          )}
        </button>

        {/* Overlay */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar Menu */}
        <nav
          ref={menuRef}
          className={`
            fixed top-0 ${sidebarSide} h-full w-64 bg-slate-800 ${borderClass} border-slate-700 z-40
            transform transition-transform duration-300 ease-in-out
            ${mobileMenuOpen ? 'translate-x-0' : translateHidden}
            ${className}
          `}
        >
          {/* Header */}
          <div className="p-4 border-b border-slate-700 flex flex-col items-center gap-2">
            {headerLogo && (
              <img
                src={headerLogo}
                alt="Logo"
                className="h-12 w-auto object-contain"
              />
            )}
            <h2 className="text-base font-bold text-white text-center">{headerTitle}</h2>
          </div>

        {/* Menu Items */}
        <div className="flex flex-col p-2 pb-20 space-y-1 overflow-y-auto h-[calc(100%-60px)]">
          {items.map(item => {
            const Icon = item.icon;
            const isActive = activeItemId === item.id;
            const hasChildren = item.children && item.children.length > 0;
            const isDropdownOpen = openDropdownId === item.id;

            return (
              <div key={item.id}>
                <button
                  onClick={() => {
                    handleItemClick(item);
                    // Chiudi menu solo se non ha children (se ha children si apre dropdown)
                    if (!hasChildren) {
                      setMobileMenuOpen(false);
                    }
                  }}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
                    ${isActive
                    ? 'bg-blue-600 text-white font-semibold'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }
                `}
              >
                {Icon && <Icon size={18} />}
                <span className="flex-1 text-left">{item.label}</span>
                {hasChildren && (
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                  />
                )}
              </button>

              {/* Dropdown inline */}
              {hasChildren && isDropdownOpen && (
                <div className="ml-6 mt-1 space-y-1 animate-in slide-in-from-top-2 fade-in duration-200">
                  {item.children!.map(child => {
                    const ChildIcon = child.icon;
                    const isChildActive = activeItemId === child.id;

                    return (
                      <button
                        key={child.id}
                        onClick={() => {
                          handleChildClick(child);
                          setMobileMenuOpen(false); // Chiudi menu dopo click
                        }}
                        className={`
                          w-full flex items-center gap-3 px-4 py-2 rounded-lg text-left transition-colors
                          ${isChildActive
                            ? 'bg-blue-600/30 text-blue-300 font-semibold'
                            : 'text-slate-300 hover:bg-slate-600 hover:text-white'
                          }
                        `}
                      >
                        {ChildIcon && <ChildIcon size={16} />}
                        <span className="text-sm">{child.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        </div>
      </nav>
    </>
    );
  };

  // HAMBURGER MOBILE
  const renderHamburger = () => (
    <div ref={menuRef} className={`relative ${className}`}>
      {/* Hamburger button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="flex items-center gap-2 px-4 py-3 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors"
      >
        {mobileMenuOpen ? <X size={24} /> : <MenuIcon size={24} />}
        <span className="font-semibold">Menu</span>
      </button>

      {/* Dropdown menu */}
      {mobileMenuOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 max-h-[80vh] overflow-y-auto animate-in slide-in-from-top-4 fade-in duration-200">
          {renderVertical()}
        </div>
      )}
    </div>
  );

  // Render appropriate layout
  return (
    <>
      <style>{scrollbarHideStyles}</style>
      {showHamburger ? renderHamburger() : effectiveOrientation === 'horizontal' ? renderHorizontal() : renderVertical()}
    </>
  );
};

export default AdaptiveMenu;
